import { ModerationStatus, VerificationCode, VerificationSeverity } from '@common/contracts';
import { computeDistribution } from '../DistributionPanel/distributionModel';
/** A cell counts as "filled" if it has a trial or an assigned type/icon. */
function hasAnyTrials(level) {
    return Object.values(level.nodes || {}).some((n) => { var _a, _b; return ((_b = (_a = n === null || n === void 0 ? void 0 : n.simpleEvents) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 || (n === null || n === void 0 ? void 0 : n.typeId) != null; });
}
export function computeChecklist(level, events = [], startTypeId, report) {
    var _a, _b, _c, _d;
    const status = level.moderationStatus;
    const approved = status === ModerationStatus.Approved;
    const submitted = status === ModerationStatus.Pending || approved;
    const { stats } = computeDistribution(level, events, startTypeId);
    const hasName = !!((_a = level.name) === null || _a === void 0 ? void 0 : _a.trim());
    const hasParams = level.mapWidth >= 1 && level.mapHeight >= 1 && level.moveCost >= 1;
    const hasBackground = !!level.backgroundImage;
    const hasStory = hasAnyTrials(level);
    const hasStart = stats.startPointCount === 1;
    // Solvability (from the auto-verifier): the goal must be set, reachable, and
    // reachable on the success path, with no forgotten/broken notifications. These
    // gate submission the same way the server does, so the button matches the API.
    const findings = (_b = report === null || report === void 0 ? void 0 : report.findings) !== null && _b !== void 0 ? _b : [];
    const has = (code) => findings.some(f => f.code === code);
    const hasGoal = report ? (!!report.stats.goalCoord && !has(VerificationCode.MultiGoal)) : true;
    const goalErrorCodes = [VerificationCode.GoalUndefined, VerificationCode.MultiGoal, VerificationCode.GoalUnreachable, VerificationCode.GoalUnreachableOnSuccess];
    const goalError = findings.find(f => goalErrorCodes.includes(f.code) && f.severity === VerificationSeverity.Error);
    const pathOk = report ? !goalError : true;
    const orphanPanels = findings.filter(f => f.code === VerificationCode.OrphanPanel || f.code === VerificationCode.UnreachablePanel);
    const panelsOk = orphanPanels.length === 0;
    // Hard product rule: the spawn cell may never carry an event — the game
    // engine cannot run one there. Surfaces live on the "start" item below.
    const trialOnStart = findings.find(f => f.code === VerificationCode.TrialOnStart);
    // Overall: when a report exists, block on it being solvable; before the first
    // verify runs we don't block (the editor auto-verifies on open).
    const verifyOk = report ? report.solvable : true;
    // Challenge entries carry exact distribution targets (>0). When a target is
    // set, the item tracks distributed/target: under = todo, exact = done, over =
    // over (a problem). Free levels keep the original "at least one" rule.
    const targetKnowledge = (_c = level.targetKnowledge) !== null && _c !== void 0 ? _c : 0;
    const targetResources = (_d = level.targetResources) !== null && _d !== void 0 ? _d : 0;
    const knowledge = distributionItem('knowledge', 'Distribute data', 'Data', stats.knowledgeTotal, targetKnowledge, stats.knowledgeTotal > 0, 'At least one obstacle or cell must give Data.');
    // Resources are secondary to Data and no longer a submission requirement for
    // free levels, so the "Distribute resources" row only appears when a challenge
    // sets an explicit resource target (a hard constraint that must still be met).
    const resourcesTracked = targetResources > 0;
    const resources = resourcesTracked
        ? distributionItem('resources', 'Distribute resources', 'resource', stats.resourceTotal, targetResources, stats.resourceNodeCount > 0, 'At least one cell must give resources.', 'Only obstacle resource rewards count toward the target — scannable cells don\'t.')
        : null;
    const items = [
        { key: 'name', label: 'Name your Simulation', hint: 'A clear, evocative name.', status: hasName ? 'done' : 'todo' },
        { key: 'params', label: 'Set the grid parameters', hint: 'Size, move cost, avatar limits.', status: hasParams ? 'done' : 'todo' },
        // COMMENTED OUT — a level can no longer be created without its map (the wizard's
        // Background step is now a mandatory upload), so "Add a backdrop" is always
        // already satisfied and only added noise. `hasBackground` is still used below.
        // { key: 'background', label: 'Add a backdrop', hint: 'The image shown under the grid. Optional — upload now, skip, or render it with AI after approval.', status: hasBackground ? 'done' : 'optional' },
        { key: 'story', label: 'Seed the world with obstacles', hint: 'Forge with AI, or fill cells by hand.', status: hasStory ? 'done' : 'todo' },
        {
            key: 'start',
            label: 'Mark where Stalkers dive in',
            hint: trialOnStart
                ? trialOnStart.message
                : stats.startPointCount > 1
                    ? 'Only one cell may be the Start.'
                    : 'Give one cell the "Start" node type — where Stalkers materialize.',
            // An event parked on the start is a hard error (the game can't run it
            // there) — shown as a warning state, and it blocks submission.
            status: trialOnStart ? 'over' : hasStart ? 'done' : 'todo',
        },
        { key: 'goal', label: 'Mark the goal', hint: report && has(VerificationCode.MultiGoal) ? 'Only one cell may be the goal.' : 'Give one cell the goal flag — where the story culminates.', status: hasGoal ? 'done' : 'todo' },
        {
            key: 'path',
            label: 'A clear path to the goal',
            hint: goalError ? goalError.message : 'Start → goal is reachable, and success opens the whole way.',
            status: pathOk ? 'done' : 'todo',
        },
        {
            key: 'panels',
            label: 'No forgotten notifications',
            hint: panelsOk ? 'Every UX panel is shown by an obstacle.' : orphanPanels[0].message,
            status: panelsOk ? 'done' : 'todo',
        },
        knowledge.item,
        ...(resources ? [resources.item] : []),
        {
            key: 'submit',
            label: 'Submit for review',
            hint: submitted ? 'Submitted — awaiting review.' : 'Send it up for review.',
            status: submitted ? 'done' : 'todo',
        },
        {
            key: 'image',
            // A level that already has a backdrop (e.g. uploaded at creation) has its
            // world image — this step is satisfied and never shown locked. Otherwise
            // it unlocks for rendering once the level is approved.
            label: 'Render the world image',
            hint: hasBackground ? 'The world already has a backdrop.'
                : approved ? 'Draft, then Finalize the world art.'
                    : 'Unlocks once your Simulation is verified.',
            status: hasBackground ? 'done' : approved ? 'todo' : 'locked',
        },
    ];
    const required = items.filter((i) => i.status !== 'optional');
    const doneCount = required.filter((i) => i.status === 'done').length;
    const canSubmit = hasName && hasParams && hasStory && hasStart
        && hasGoal && pathOk && panelsOk && verifyOk
        && knowledge.ok && (!resources || resources.ok) && !submitted;
    return { items, doneCount, totalCount: required.length, canSubmit };
}
// Build a distribution checklist item. With an exact target (>0): under = todo,
// exact = done, over = 'over' (a problem). Without a target: the original
// "at least one" rule via `fallbackOk`. `ok` gates submission.
function distributionItem(key, label, unit, current, target, fallbackOk, fallbackHint, targetClarifier = '') {
    if (target > 0) {
        const status = current === target ? 'done' : current > target ? 'over' : 'todo';
        const base = status === 'over' ? `${current} / ${target} — exceeds the target by ${current - target}. Remove some ${unit}.`
            : status === 'done' ? `${current} / ${target} — target met.`
                : `${current} / ${target} — distribute ${target - current} more.`;
        const hint = targetClarifier && status !== 'done' ? `${base} ${targetClarifier}` : base;
        return { item: { key, label, hint, status }, ok: status === 'done' };
    }
    return { item: { key, label, hint: fallbackHint, status: fallbackOk ? 'done' : 'todo' }, ok: fallbackOk };
}
